/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TrendingUp, Coins, Wallet, Shield, Rocket, Loader2, Info, BarChart3,
} from "lucide-react";
import { apiGet } from "@/lib/api";

// ---- Scenario metadata ----------------------------------------------------
//
// Mirrors the CSE Projection tab visual treatment 1:1 — same icons,
// blurbs, accent classes — only the descriptive subtext changes to
// reflect the US data model (analyst targets, S&P sector PE
// convergence) rather than CSE's specific assumptions.

const SCENARIOS = [
  {
    key: "conservative",
    label: "Conservative",
    Icon: Shield,
    accent: "text-muted-foreground",
    bg: "bg-muted/40",
    border: "border-muted-foreground/30",
    blurb: "Half-way to base target · 0% dividend growth",
  },
  {
    key: "base_case",
    label: "Base Case",
    Icon: TrendingUp,
    accent: "text-primary",
    bg: "bg-primary/5",
    border: "border-primary/30",
    blurb: "Composite-score base target · 5y CAGR dividends",
  },
  {
    key: "optimistic",
    label: "Optimistic",
    Icon: Rocket,
    accent: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-500/5",
    border: "border-emerald-500/30",
    blurb: "Analyst mean target · 1.5× CAGR dividends",
  },
];

// ---- Row schema ----------------------------------------------------------
//
// Flat list of rows rendered into the projection grid. Section headers are
// pseudo-rows whose only purpose is to break up the table visually.

const ROWS = [
  { key: "_section_dividend", section: "Dividend Income" },
  { key: "annual_dividend_year_1", label: "Year 1 Annual Dividend", Icon: Coins, format: "usd" },
  { key: "annual_dividend_year_n", label: (y) => `Year ${y} Annual Dividend`, Icon: Coins, format: "usd" },
  { key: "total_dividends_period", label: (y) => `${y}-Year Total Dividends`, Icon: Coins, format: "usd" },

  { key: "_section_capital", section: "Capital Growth" },
  { key: "entry_value", label: "Entry value (initial investment)", Icon: Wallet, format: "usd" },
  { key: "capital_value_at_end", label: (y) => `Exit value at Year ${y}`, Icon: Wallet, format: "usd" },
  { key: "capital_gain", label: (y) => `Capital gain over ${y} years`, Icon: TrendingUp, format: "usd_pct", pctKey: "capital_gain_pct" },

  { key: "_section_total", section: "TOTAL Return (Dividends + Capital)" },
  { key: "total_return", label: "Total return (USD)", Icon: TrendingUp, format: "usd_pct", pctKey: "total_return_pct" },
  { key: "annualised_return_pct", label: "Annualised return", Icon: TrendingUp, format: "pct" },
];

// ---- Formatters ----------------------------------------------------------

const fmtUsd = (value, { compact = true } = {}) => {
  if (value == null || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (compact) {
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  }
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const fmtPct = (value, decimals = 1) => {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value >= 0 ? "" : ""}${value.toFixed(decimals)}%`;
};

// ---- Main component ------------------------------------------------------

const UsReturnProjectionPanel = ({ portfolioId, testIdPrefix = "us-projection" }) => {
  const [years, setYears] = useState(5);
  const [reinvest, setReinvest] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet(
        `/us-portfolios/${portfolioId}/projection?years=${years}&reinvest_dividends=${reinvest}`,
      );
      setData(res || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load projection");
    } finally {
      setLoading(false);
    }
  }, [portfolioId, years, reinvest]);

  useEffect(() => { load(); }, [load]);

  const empty = !loading && !data?.holdings?.length;
  const scenarios = data?.scenarios || {};
  const sp = data?.sp500_benchmark;
  const winnerKey = useMemo(() => {
    if (!scenarios?.base_case) return null;
    let best = null; let bestVal = -Infinity;
    for (const s of SCENARIOS) {
      const ret = scenarios[s.key]?.annualised_return_pct;
      if (ret != null && ret > bestVal) { best = s.key; bestVal = ret; }
    }
    return best;
  }, [scenarios]);

  const renderCell = (row, scenarioKey) => {
    const sc = scenarios?.[scenarioKey];
    if (!sc) return <span className="text-muted-foreground">—</span>;
    const v = sc[row.key];
    if (v == null) return <span className="text-muted-foreground">—</span>;
    if (row.format === "usd") return fmtUsd(v);
    if (row.format === "pct") return fmtPct(v, 2);
    if (row.format === "usd_pct") {
      const p = sc[row.pctKey];
      const tone = v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive";
      return (
        <span className={tone}>
          {v >= 0 ? "+" : ""}{fmtUsd(v)}
          {p != null && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({p >= 0 ? "+" : ""}{p.toFixed(1)}%)
            </span>
          )}
        </span>
      );
    }
    return String(v);
  };

  return (
    <Card className="border bg-card" data-testid={`${testIdPrefix}-panel`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Projected Returns
            </CardTitle>
            <CardDescription>
              Three-scenario {years}-year projection — dividend income + capital appreciation.
              All amounts in USD.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label htmlFor={`${testIdPrefix}-reinvest`} className="text-xs cursor-pointer">
                💰 Reinvest dividends
              </Label>
              <Switch
                id={`${testIdPrefix}-reinvest`}
                checked={reinvest}
                onCheckedChange={setReinvest}
                data-testid={`${testIdPrefix}-reinvest-toggle`}
              />
            </div>
            <div className="flex items-center gap-2 ml-2 border-l pl-3">
              <Label className="text-xs">Years</Label>
              <div className="inline-flex rounded-md border bg-muted/30 p-0.5 text-xs">
                {[3, 5, 10].map((y) => (
                  <button
                    key={y}
                    onClick={() => setYears(y)}
                    data-testid={`${testIdPrefix}-years-${y}`}
                    className={`px-2.5 py-0.5 rounded transition-colors ${
                      years === y
                        ? "bg-background shadow-sm font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {y}y
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <span className="text-destructive">{error}</span>
          </div>
        ) : null}

        {loading && !data ? (
          <div className="space-y-3">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : empty ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
            Add some US holdings first to see projected returns.
          </div>
        ) : (
          <>
            {/* Scenario headline cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`${testIdPrefix}-scenario-cards`}>
              {SCENARIOS.map((s) => {
                const sc = scenarios[s.key];
                const annual = sc?.annualised_return_pct;
                const totalRet = sc?.total_return_pct;
                const isWinner = winnerKey === s.key;
                return (
                  <div
                    key={s.key}
                    className={`rounded-lg border p-3 ${s.bg} ${s.border} ${isWinner ? "ring-2 ring-primary/40" : ""}`}
                    data-testid={`${testIdPrefix}-scenario-${s.key}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <s.Icon className={`h-4 w-4 ${s.accent}`} />
                      <span className={`text-sm font-semibold ${s.accent}`}>{s.label}</span>
                      {isWinner ? (
                        <Badge variant="outline" className="ml-auto text-[10px] bg-background">Best</Badge>
                      ) : null}
                    </div>
                    <div className="text-2xl font-semibold tabular-nums">
                      {annual != null ? `${annual.toFixed(1)}%` : "—"}
                      <span className="text-xs text-muted-foreground ml-1">/yr</span>
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      Total: {fmtPct(totalRet, 1)} over {years}y
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
                      {s.blurb}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Detailed projection table */}
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium" />
                    {SCENARIOS.map((s) => (
                      <th key={s.key} className={`text-right px-3 py-2 font-medium ${s.accent}`}>
                        <div className="flex items-center justify-end gap-1.5">
                          <s.Icon className="h-3 w-3" />
                          {s.label}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => {
                    if (row.section) {
                      return (
                        <tr key={row.key} className="bg-muted/30 border-y border-border">
                          <td colSpan={4} className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                            {row.section}
                          </td>
                        </tr>
                      );
                    }
                    const labelText = typeof row.label === "function" ? row.label(years) : row.label;
                    return (
                      <tr key={row.key} className="border-t border-border">
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            {row.Icon ? <row.Icon className="h-3 w-3" /> : null}
                            {labelText}
                          </span>
                        </td>
                        {SCENARIOS.map((s) => (
                          <td
                            key={s.key}
                            className="px-3 py-2 text-right tabular-nums text-sm"
                            data-testid={`${testIdPrefix}-cell-${s.key}-${row.key}`}
                          >
                            {renderCell(row, s.key)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* S&P 500 benchmark row */}
            {sp ? (
              <div
                className="rounded-lg border border-dashed bg-muted/30 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`${testIdPrefix}-sp500`}
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-xs font-medium">S&amp;P 500 historical avg (10%/yr) — passive benchmark</div>
                    <div className="text-[11px] text-muted-foreground">
                      Same {fmtUsd(sp.entry_value)} invested in SPY would compound to {fmtUsd(sp.exit_value)} over {years} years.
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold tabular-nums">
                    {fmtUsd(sp.total_return)}
                    <span className={`ml-1 text-xs ${sp.total_return_pct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                      ({fmtPct(sp.total_return_pct, 1)})
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {scenarios?.base_case?.annualised_return_pct != null ? (
                      scenarios.base_case.annualised_return_pct >= 10 ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          Your base case beats SPY by {(scenarios.base_case.annualised_return_pct - 10).toFixed(1)} pts/yr
                        </span>
                      ) : (
                        <span className="text-destructive">
                          SPY would beat your base case by {(10 - scenarios.base_case.annualised_return_pct).toFixed(1)} pts/yr
                        </span>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Pre-tax disclaimer */}
            <div
              className="rounded-md border bg-muted/40 p-3 flex items-start gap-2 text-xs text-muted-foreground"
              data-testid={`${testIdPrefix}-tax-note`}
            >
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Returns shown pre-tax.</p>
                <p>
                  Tax treatment varies by country and account type (taxable brokerage,
                  IRA, ISA, etc.). Consult a tax advisor for net-of-tax projections.
                  US-domiciled stocks are typically subject to a 30% dividend
                  withholding tax for non-US residents (15% under DTAA agreements).
                </p>
              </div>
            </div>

            {loading ? (
              <div className="text-center text-xs text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UsReturnProjectionPanel;
